import React, { useCallback, useState } from 'react';
import {
  KoobContainer,
  KoobPageHeader,
  KoobSkeleton,
  KoobTitle,
} from '@koob/ui';
import { useAsync } from 'react-async';
import {
  addGuestsToFolder,
  archiveFolder,
  getFolder,
  unarchiveFolder,
  updateFolderInfo,
  updateGuestsToFolder,
} from '../../api/folders';
import { getLanguages } from '../../api/languages';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useBooking,
  useError,
  useExperienceBooking,
  useLanguage,
  useSnack,
} from '../../hooks';
import { useSetTripFilters } from '@koob/trip-designer';

import { decodeId, KoobStateEnum } from 'utils';
import { AuditLogResource } from '@koob/audit-log';
import { getResourceAuditLogs } from 'api/auditLogs';
import { camelCase } from 'lodash';
import { openTripWizard } from '@koob/ui/src/features/tripWizard';
import { TripHistory } from './Partials/TripHistory';
import FoldersDetailsActions from './Partials/FoldersDetailsActions';
import KoobLayout from '../../components/Koob/KoobLayout';
import FoldersBookingsTable from './Partials/FoldersBookingsTable';
import FoldersSummary from './Partials/FoldersSummary';
import {
  buildFolderSummary,
  folderPax,
  folderRoomsComposition,
  getFolderBaseStatus,
  getFolderStatus,
} from '@koob/utils';
import { useKoobStore } from 'store';
import {
  FolderArchivedBanner,
  FolderArchiveModal,
  FolderCollapsibleSection,
  FolderEditDetailsModal,
  FolderLanguageBadge,
  FolderStatusBadge,
  FolderTravelerModal,
  FolderTravelersTable,
} from '@koob/booking';

export default function FoldersDetails() {
  const { t } = useTranslation('folders');
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { setCustomerFile, setFilters, nationalities } = useBooking();
  const { setExperienceFilters } = useExperienceBooking();
  const setTripFilters = useSetTripFilters();
  const { setLang } = useLanguage();
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isProposalTrip, setIsProposalTrip] = useState(false);
  const [activityCount, setActivityCount] = useState(null);
  // Bumped on each folder load: the activity log reads again after an action
  // on this page (KD-3910).
  const [activityVersion, setActivityVersion] = useState(0);

  // Hands the logs to the timeline and keeps their total for the card header,
  // as the mockup shows it. Stable, or the timeline would fetch again.
  const fetchActivityLogs = useCallback(async (type, id, query) => {
    const logs = await getResourceAuditLogs(type, id, query);
    setActivityCount(logs?.total ?? null);
    return logs;
  }, []);
  const [travelerModalOpen, setTravelerModalOpen] = useState(false);
  // The traveler being edited; null while adding one.
  const [editedTraveler, setEditedTraveler] = useState(null);
  const activeFolderId = useKoobStore(s => s.activeFolder?.id);
  const refreshFolder = useKoobStore(s => s.refreshFolder);
  const closeFolder = useKoobStore(s => s.closeFolder);
  const openFolder = useKoobStore(s => s.openFolder);
  // Same gate as the navbar "Design a trip" entry (KoobLayout keeps it fresh).
  const canDesignTrip =
    localStorage.getItem('tripDesignerRequestState') ===
    KoobStateEnum.VALIDATED;

  const { data, reload, isLoading } = useAsync({
    promiseFn: getFolder,
    id: folderId,
    onResolve: data => {
      setTripFilters(prev => ({
        ...prev,
        clientFolder: data?.data,
      }));
      setIsProposalTrip(data?.data?.trips.some(item => item.isProposal));
      setActivityVersion(version => version + 1);
    },
  });

  const { data: languagesData } = useAsync({ promiseFn: getLanguages });

  const folder = data?.data ?? null;
  const archived = Boolean(folder?.archivedAt);
  const folderLanguage = (languagesData?.data ?? []).find(
    language => language.name === folder?.locale,
  );

  // Reload the page copy and, when this is the active folder, the store copy
  // the sidebar card + Hotels/Experiences flows read from — otherwise it stays
  // stale until you close/open the folder again.
  const reloadFolder = () => {
    reload();
    if (activeFolderId && String(activeFolderId) === String(folderId)) {
      refreshFolder();
    }
  };

  // Adult / Child and the passport warning read the trip departure and return.
  const { stay } = buildFolderSummary(folder);

  const openTravelerModal = traveler => {
    setEditedTraveler(traveler);
    setTravelerModalOpen(true);
  };

  const saveTraveler = async traveler => {
    try {
      if (traveler.id) {
        await updateGuestsToFolder(folder.id, [traveler]);
        notify(t('folderUpdatedSuccess'));
      } else {
        await addGuestsToFolder({ id: folder.id, travelers: [traveler] });
        notify(t('paxAddedSuccess'));
      }
      setTravelerModalOpen(false);
      reloadFolder();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  const rooms = folderRoomsComposition(folder);
  const expPax = folderPax(folder);

  const handleBookingStart = goTo => {
    if (folder?.locale) {
      setLang(folder.locale);
    }
    setCustomerFile(folder);
    // KD-3910: the hotel and experience searches book into the active folder
    // and ask for one when there is none.
    if (String(activeFolderId) !== String(folder.id)) {
      openFolder(folder);
    }
    setExperienceFilters(current => ({
      ...current,
      clientFolder: folder,
      experienceComposition: [expPax],
    }));

    setFilters(current => ({
      ...current,
      rooms,
    }));
    navigate(goTo);
  };

  // A proposal keeps its exchange history (the former "History" tab).
  const proposalHistories = isProposalTrip
    ? folder?.trips?.find(trip => trip.draft)?.tripHistory
    : null;

  const handleArchive = async () => {
    try {
      await archiveFolder(folder.id);
      // An archived folder is read-only: it cannot stay the active folder.
      if (activeFolderId && String(activeFolderId) === String(folder.id)) {
        closeFolder();
      }
      notify(t('archive.success'));
      setShowArchiveModal(false);
      reload();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  const handleUnarchive = async () => {
    try {
      await unarchiveFolder(folder.id);
      notify(t('archive.unarchiveSuccess'));
      reloadFolder();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  const updateFolder = async values => {
    try {
      await updateFolderInfo(folder?.id, values);
      notify(t('folderUpdatedSuccess'));
      setShowEditModal(false);
      reloadFolder();
    } catch (err) {
      // KD-3842: the chosen main guest is not an adult of this folder.
      if (err?.response?.status === 400 && values.mainTravelerId) {
        notify(t('editDetails.mainGuestInvalid'), { type: 'error' });
        return;
      }
      // KD-3645 — the API answers a duplicate ICR with a raw English sentence
      // and no stable error code, so `camelCase(message)` built a key the
      // `errors` namespace does not hold and the snack printed that key
      // verbatim. Map the statuses instead, like the creation modal does.
      if (err?.response?.status === 409) {
        // The API only runs its ICR uniqueness check when the submitted ICR
        // differs from the stored one, so a conflict on an untouched ICR comes
        // from somewhere else and must not blame a field the user never edited.
        const icrChanged = values.icr !== folder?.icr;
        notify(
          t(icrChanged ? 'details.existingIcr' : 'details.updateConflict'),
          {
            type: 'error',
          },
        );
        return;
      }
      if (err?.response?.status === 500) {
        notify(t('details.updateFailed'), { type: 'error' });
        return;
      }
      const error = camelCase(err?.response?.data?.message ?? 'error');
      notify(t(`errors:${error}`), { type: 'error' });
    }
  };

  return (
    <KoobLayout
      header={
        <KoobPageHeader
          title={
            folder
              ? folder.name || folder.icr || t('details.screenTitle')
              : undefined
          }
          subtitle={
            folder && (
              <span className="flex flex-wrap items-center gap-2.5">
                {folder.name && folder.icr && <span>{folder.icr}</span>}
                <FolderStatusBadge
                  status={getFolderStatus(folder)}
                  previousStatus={
                    archived ? getFolderBaseStatus(folder) : undefined
                  }
                />
                {folder.locale && (
                  <FolderLanguageBadge
                    label={folderLanguage?.title ?? folder.locale.toUpperCase()}
                    flag={folderLanguage?.description}
                  />
                )}
              </span>
            )
          }
          breadcrumbs={[{ label: t('title'), to: '/folders' }]}
          actions={
            folder && (
              <FoldersDetailsActions
                folder={folder}
                canDesignTrip={canDesignTrip}
                onEditDetails={() => setShowEditModal(true)}
                onArchive={() => setShowArchiveModal(true)}
                onUnarchive={handleUnarchive}
                onAddHotel={() => handleBookingStart('/hotels')}
                onAddExperience={() => handleBookingStart('/experiences')}
                onDesignTrip={() => openTripWizard()}
                reload={reloadFolder}
              />
            )
          }
        />
      }
    >
      <KoobContainer width="ultrawide" padding="sm">
        <div className="flex flex-col gap-5">
          {archived && (
            <FolderArchivedBanner
              folder={folder}
              onUnarchive={handleUnarchive}
            />
          )}

          <div className="flex flex-col gap-5">
            {isLoading && <KoobSkeleton shape="details" />}

            {folder && (
              <>
                <FoldersSummary folder={folder} />

                <FolderTravelersTable
                  travelers={folder.travelers ?? []}
                  departureDate={stay.startAt}
                  returnDate={stay.endAt}
                  readOnly={archived}
                  onEdit={openTravelerModal}
                  onAdd={() => openTravelerModal(null)}
                />

                <FoldersBookingsTable folder={folder} archived={archived} />

                {proposalHistories?.length > 0 && (
                  <div>
                    <KoobTitle size="text-2xl">
                      {t('bookings.tripHistory')}
                    </KoobTitle>
                    <TripHistory histories={proposalHistories} />
                  </div>
                )}

                {/* KD-3854: the folder's activity log, in the mockup's card. */}
                <FolderCollapsibleSection
                  title={t('activityLog.title')}
                  count={
                    activityCount === null
                      ? null
                      : t('activityLog.events', { count: activityCount })
                  }
                  flush
                  testId="toggle-folder-activity-log"
                >
                  <AuditLogResource
                    key={activityVersion}
                    type="folders"
                    id={decodeId(folderId)}
                    fetchResourceAuditLogs={fetchActivityLogs}
                    perPage={20}
                    showCount={false}
                    embedded
                  />
                </FolderCollapsibleSection>
              </>
            )}
          </div>
        </div>

        {folder && (
          <>
            <FolderTravelerModal
              open={travelerModalOpen}
              setOpen={setTravelerModalOpen}
              traveler={editedTraveler}
              departureDate={stay.startAt}
              returnDate={stay.endAt}
              nationalityOptions={nationalities ?? []}
              onSubmit={saveTraveler}
            />

            <FolderEditDetailsModal
              open={showEditModal}
              setOpen={setShowEditModal}
              folder={folder}
              onSubmit={updateFolder}
            />

            <FolderArchiveModal
              open={showArchiveModal}
              setOpen={setShowArchiveModal}
              folderName={folder.name}
              onConfirm={handleArchive}
            />
          </>
        )}
      </KoobContainer>
    </KoobLayout>
  );
}
